import { useCallback, useMemo, useState } from 'react';
import isEmpty from 'validator/lib/isEmpty';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import useDebounceState from '@/hooks/useDebounceState';
import useEntitySet from '@/hooks/useEntitySet';
import useResponse from '@/hooks/useResponse';
import {
  FileCategoryEnum,
  FilePurposeEnum,
  VisibilitySettingsAccessTypeEnum,
} from '@/types/schema';

import { useCreateCategoryMutation } from '../graphql/mutations/generated/createCategory';
import { useUpdateCategoryMutation } from '../graphql/mutations/generated/updateCategory';
import {
  CategoryActionsEnum,
  CategoryDialogActions,
  CategoryDialogsEnum,
  CategoryDialogsState,
  CategoryLayoutView,
  CategoryType,
} from '../types';
import { getCategoryActionMenuItems } from '../utils';
import useCategoryPermissions from './useCategoryPermissions';
import useDeleteCategory from './useDeleteCategory';
import useMergeCategory from './useMergeCategory';

interface UseCategoryActionsProps {
  layoutView: CategoryLayoutView;
  selectedIds: string[];
}

function useCategoryActions({ layoutView, selectedIds }: UseCategoryActionsProps) {
  const { t } = useTranslation();
  const [{ fetching: fetchingOnCreate }, executeCreateCategory] = useCreateCategoryMutation();
  const [, executeUpdateCategory] = useUpdateCategoryMutation();
  const { selectedItemsSet, has, toggle, reset } = useEntitySet(selectedIds);
  const [, setEditingCategory, editingCategory] = useDebounceState<string | null>(null);
  const { handleResponse } = useResponse();
  const [dialogState, setDialogState] = useState<CategoryDialogsState | null>(null);
  const [singleSelectCategory, setSingleSelectCategory] = useState<string | null>(null);
  const { permissions, showPermissionError } = useCategoryPermissions();

  const closeDialog = useEvent(() => setDialogState(null));

  const { fetching: fetchingDelete, onDelete } = useDeleteCategory({
    onSuccess: closeDialog,
  });

  const { fetching: fetchingMerge, onMerge } = useMergeCategory({
    onSuccess: closeDialog,
  });

  const actionMenuItems = getCategoryActionMenuItems(t);

  const selectedItems = useMemo(() => {
    if (layoutView === 'single-select') {
      return singleSelectCategory ? [singleSelectCategory] : [];
    }
    return Array.from(selectedItemsSet);
  }, [layoutView, selectedItemsSet, singleSelectCategory]);

  const onAddCategory = useEvent(async (parentId?: string, onSuccess?: () => void) => {
    const result = await executeCreateCategory({
      data: {
        category: FileCategoryEnum.File,
        purpose: FilePurposeEnum.LibraryCategory,
        name: '',
        parentId,
        visibilitySettings: {
          accessType: VisibilitySettingsAccessTypeEnum.Open,
        },
      },
    });
    handleResponse({
      condition: Boolean(result.data?.createFile?.file?.id),
      onSuccess: () => {
        setEditingCategory(result.data?.createFile?.file?.id!);
        onSuccess?.();
      },
    });
  });

  const onNameChange = useEvent(async (id: string, name: string) => {
    const result = await executeUpdateCategory({
      data: {
        fileId: id,
        name: isEmpty(name.trim()) ? t('Discover:Category.Admin.UntitledCategory') : name.trim(),
      },
    });
    setEditingCategory(null);
  });

  const onAction = useEvent(
    (actionType: CategoryActionsEnum, category: CategoryType, onSuccess?: () => void) => {
      switch (actionType) {
        case CategoryActionsEnum.AddSubcategory:
          if (permissions.canCreate) onAddCategory(category.id, onSuccess);
          else showPermissionError('create');
          break;
        case CategoryActionsEnum.RenameCategory:
          if (permissions.canEdit) setEditingCategory(category.id);
          else showPermissionError('rename');
          break;
        case CategoryActionsEnum.MergeCategory:
          if (permissions.canEdit)
            setDialogState({
              inViewDialog: CategoryDialogsEnum.MergeCategory,
              selectedCategory: category,
            });
          else showPermissionError('edit');
          break;
        case CategoryActionsEnum.DeleteCategory:
          if (permissions.canRemove)
            setDialogState({
              inViewDialog: CategoryDialogsEnum.DeleteCategory,
              selectedCategory: category,
            });
          else showPermissionError('delete');
          break;
      }
    },
  );

  const onDialogActions = useEvent((props: CategoryDialogActions) => {
    switch (props.action) {
      case 'delete':
        {
          const { category } = props.payload;
          onDelete(category);
        }
        break;
      case 'merge':
        {
          const { category, newParentCategory } = props.payload;
          onMerge(category, newParentCategory);
        }
        break;
      case 'close':
        setDialogState(null);
        break;
    }
  });

  const onToggleSelection = useEvent((key: string) => {
    if (layoutView === 'single-select') {
      setSingleSelectCategory((prevValue) => (prevValue === key ? null : key));
    } else {
      toggle(key);
    }
  });

  const isSelected = useCallback(
    (key: string): boolean => {
      if (layoutView === 'single-select') {
        return singleSelectCategory === key;
      } else {
        return has(key);
      }
    },
    [has, layoutView, singleSelectCategory],
  );

  return {
    layoutView,
    selectedItems,
    state: {
      editingCategory,
      actionMenuItems,
      dialogState,
      fetchingOnActions: fetchingDelete || fetchingMerge,
      fetchingOnCreate,
    },
    actions: {
      isSelected,
      toggleSelection: onToggleSelection,
      onAddCategory,
      onAction,
      onNameChange,
      onDialogActions,
    },
  };
}

export default useCategoryActions;
